import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Divider, Grid, Typography} from '@mui/material';

import {CompanyDepartmentDetailPane} from './CompanyDepartmentDetailPane';
import {CompanyDepartmentList} from './CompanyDepartmentList';
import {CompanyDepartmentModal} from './CompanyDepartmentModal';
import {DepartmentFormValues} from './types/CompanyDepartmentFormTypes';
import EmpionDialog from '../../../../../components/EmpionDialog/EmpionDialog';
import {CompanyContext} from '../../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiRead} from '../../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../../hooks/useApiWrite';
import {Delete} from '../../../../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../../../../lib/api/DELETE/DeleteRoutes';
import {GetRoutes} from '../../../../../lib/api/GET/GetRoutes';
import {Patch} from '../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../lib/api/PATCH/PatchRoutes';
import {Post} from '../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../lib/api/POST/PostRoutes';
import {CompanyProfileRequestBodies} from '../../../../../lib/api/RequestBodies/features/company/profile/CompanyProfileRequestBodies';
import {ModuleTypes} from '../../../../../types/CompanyProductConfiguration/CompanyModule';
import {MethodDepartmentCategoryResponse} from '../../../../../types/MethodDepartmentCategory';
import {capitalizeFirstLetter} from '../../../../../utils/capitalizeFirstLetter';
import {isCompanyModuleActive} from '../../../../../utils/companyModuleUtils/isCompanyModuleActive';
import {Department} from '../../../profile/types';
import {DEFAULT_METHOD_TEXT_PROFILE, MethodTextProfileIdentifier} from '../../types/MethodTextProfileTypes';

const toFormValues = (department: Department): DepartmentFormValues => ({
  select: department.methodDepartmentCategory?.resourceId || '',
  labelDe: department.name.de,
  labelEn: department.name.en,
  methodTextProfile: (department.methodTextProfile as MethodTextProfileIdentifier) ?? DEFAULT_METHOD_TEXT_PROFILE,
});

export const CompanyDepartments = () => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'companySettings.departments'});
  const {t: tShared} = useTranslation();
  const {departments, company, productConfiguration} = useContext(CompanyContext);
  const {dispatchMessage} = useContext(SnackBarContext);

  const [displayModal, setDisplayModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [departmentIdToDelete, setDepartmentIdToDelete] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const pendingSelectNameEnRef = useRef<string | null>(null);
  const pendingSelectAfterDeleteRef = useRef<string | null>(null);

  const companyId = company.data?.resourceId || '';
  const departmentList = useMemo(() => departments.data ?? [], [departments.data]);
  const isWorkforceModuleActive = isCompanyModuleActive(
    ModuleTypes.WorkforceModule,
    productConfiguration.data?.companyModules
  );

  const selectedDepartment = useMemo(
    () => departmentList.find((department) => department.resourceId === selectedDepartmentId) ?? null,
    [departmentList, selectedDepartmentId]
  );

  const closeModal = useCallback(() => {
    setDisplayModal(false);
    setEditingDepartment(null);
  }, []);

  const methodDepartments = useApiRead<MethodDepartmentCategoryResponse>({
    path: GetRoutes.methodDepartmentCategories,
  });

  const {
    makeRequest: deleteDepartment,
    isLoading: isDeleteLoading,
    error: isDeleteError,
  } = useApiWrite<Delete.DeleteDepartment>({
    method: 'delete',
    path: DeleteRoutes[Delete.DeleteDepartment](companyId, departmentIdToDelete),
    onSuccess: () => {
      setSelectedDepartmentId(pendingSelectAfterDeleteRef.current);
      pendingSelectAfterDeleteRef.current = null;
      departments.invalidateCache();
      setDepartmentIdToDelete('');
    },
  });

  const {makeRequest: createDepartments, error: isCreateError} = useApiWrite<
    Post.CreateCompanyDepartments,
    CompanyProfileRequestBodies['createCompanyDepartments']
  >({
    method: 'post',
    path: PostRoutes[Post.CreateCompanyDepartments](companyId),
    onSuccess: () => {
      departments.invalidateCache();
      setDisplayModal(false);
      setEditingDepartment(null);
    },
  });

  const {makeManualRequestWithParams: updateDepartment, error: isUpdateError} = useApiWrite<
    Patch.UpdateCompanyDepartment,
    CompanyProfileRequestBodies['UpdateCompanyDepartment']
  >({
    onSuccess: () => {
      departments.invalidateCache();
      setDisplayModal(false);
      setEditingDepartment(null);
    },
  });

  const openCreateModal = () => {
    setEditingDepartment(null);
    setDisplayModal(true);
  };

  const openEditModal = (department: Department) => {
    setEditingDepartment(department);
    setDisplayModal(true);
  };

  const handleDeleteDepartment = (department: Department) => {
    const currentIndex = departmentList.findIndex((item) => item.resourceId === department.resourceId);
    const nextDepartment = departmentList[currentIndex + 1] ?? departmentList[currentIndex - 1] ?? null;

    if (selectedDepartmentId === department.resourceId) {
      pendingSelectAfterDeleteRef.current = nextDepartment?.resourceId ?? null;
    } else {
      pendingSelectAfterDeleteRef.current = selectedDepartmentId;
    }

    setDepartmentIdToDelete(department.resourceId);
  };

  useEffect(() => {
    if (departmentIdToDelete && !isDeleteLoading) {
      deleteDepartment();
    }
  }, [departmentIdToDelete, isDeleteLoading, deleteDepartment]);

  useEffect(() => {
    if (departments.error) {
      dispatchMessage(tShared('shared.fetchingData.error'), 'error');
    }
  }, [departments.error, dispatchMessage, tShared]);

  useEffect(() => {
    if (isCreateError) {
      pendingSelectNameEnRef.current = null;
      dispatchMessage(tShared('shared.formSubmit.creationError'), 'error');
    } else if (isUpdateError) {
      dispatchMessage(tShared('shared.formSubmit.error'), 'error');
    } else if (isDeleteError) {
      pendingSelectAfterDeleteRef.current = null;
      if (isDeleteError.extra_details.includes('Dependent data associated')) {
        dispatchMessage(t('errorDependentData'), 'error');
        setDepartmentIdToDelete('');
      } else {
        dispatchMessage(tShared('shared.formSubmit.deletionError'), 'error');
        setDepartmentIdToDelete('');
      }
    }
  }, [isCreateError, isUpdateError, isDeleteError, dispatchMessage, t, tShared]);

  useEffect(() => {
    if (departmentList.length === 0) {
      setSelectedDepartmentId(null);
      return;
    }

    const pendingNameEn = pendingSelectNameEnRef.current;
    if (pendingNameEn) {
      const createdDepartment = departmentList.find(
        (department) => department.name.en.toLowerCase() === pendingNameEn.toLowerCase()
      );
      if (createdDepartment) {
        setSelectedDepartmentId(createdDepartment.resourceId);
        pendingSelectNameEnRef.current = null;
        return;
      }
    }

    const selectionStillExists = departmentList.some(
      (department) => department.resourceId === selectedDepartmentId
    );
    if (!selectionStillExists) {
      setSelectedDepartmentId(departmentList[0].resourceId);
    }
  }, [departmentList, selectedDepartmentId]);

  const methodDepartmentCategoriesOptions = useMemo(() => {
    return methodDepartments.data?.methodDepartmentCategories.map((method) => ({
      label: method.name[currentLanguage],
      id: method.methodDepartmentCategoryResourceId,
    }));
  }, [methodDepartments.data, currentLanguage]);

  const modalInitialValues = useMemo((): DepartmentFormValues | undefined => {
    if (!editingDepartment) {
      return {
        select: '',
        labelDe: '',
        labelEn: '',
        methodTextProfile: DEFAULT_METHOD_TEXT_PROFILE,
      };
    }

    return toFormValues(editingDepartment);
  }, [editingDepartment]);

  const onSubmit = (values: DepartmentFormValues) => {
    const isDuplicate = departmentList.some(
      (dep) =>
        dep.resourceId !== editingDepartment?.resourceId &&
        (dep.name.en.toLowerCase() === values.labelEn.toLowerCase() ||
          dep.name.de.toLowerCase() === values.labelDe.toLocaleLowerCase())
    );

    if (isDuplicate) {
      dispatchMessage(t('departmentExists'), 'error');
      return;
    }

    if (editingDepartment) {
      updateDepartment({
        method: 'patch',
        path: PatchRoutes[Patch.UpdateCompanyDepartment](companyId, editingDepartment.resourceId),
        body: {
          companyDepartment: {
            methodDepartmentCategoryId: values.select || editingDepartment.methodDepartmentCategory?.resourceId || '',
            nameEn: capitalizeFirstLetter(values.labelEn) || '',
            nameDe: capitalizeFirstLetter(values.labelDe) || '',
            methodTextProfile: values.methodTextProfile,
          },
        },
      });
      return;
    }

    pendingSelectNameEnRef.current = capitalizeFirstLetter(values.labelEn) || values.labelEn;
    createDepartments({
      companyDepartments: [
        {
          methodDepartmentCategoryResourceId: values.select || '',
          methodTextProfile: values.methodTextProfile,
          name: {
            en: capitalizeFirstLetter(values.labelEn) || '',
            de: capitalizeFirstLetter(values.labelDe) || '',
          },
        },
      ],
    });
  };

  return (
    <Grid container>
      <Grid item xs={12} mb={2}>
        <Typography variant='h3'>{t('title')}</Typography>
        <Typography variant='body1'>{t('subtitle')}</Typography>
      </Grid>
      <Divider sx={{width: '100%', marginBottom: '16px'}} />

      <Grid container spacing={2} item xs={12}>
        <Grid item xs={12} md={4}>
          <CompanyDepartmentList
            departments={departmentList}
            selectedDepartmentId={selectedDepartmentId}
            emptyLabel={t('emptyDepartments')}
            addLabel={t('addDepartment')}
            onSelect={setSelectedDepartmentId}
            onAdd={openCreateModal}
            onEdit={openEditModal}
            onDelete={handleDeleteDepartment}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <CompanyDepartmentDetailPane
            companyId={companyId}
            selectedDepartment={selectedDepartment}
            isWorkforceModuleActive={isWorkforceModuleActive}
          />
        </Grid>
      </Grid>

      <EmpionDialog
        showCloseButton
        open={displayModal}
        onClose={closeModal}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '736px',
              padding: '32px',
              borderRadius: '16px',
            },
          },
        }}
      >
        <CompanyDepartmentModal
          header={editingDepartment ? t('modal.editDepartment') : t('modal.addDepartment')}
          options={methodDepartmentCategoriesOptions}
          onClose={closeModal}
          selectLabel={t('modal.fieldOfWorkSelectTitle') || ''}
          germanLabel={t('modal.labelDe') || ''}
          englishLabel={t('modal.labelEn') || ''}
          methodTextProfileLabel={t('modal.methodTextProfile') || ''}
          tooltip={t('modal.tooltipText') || ''}
          helperText={t('modal.helperText') || ''}
          onSubmit={onSubmit}
          open={displayModal}
          initialValues={modalInitialValues}
        />
      </EmpionDialog>
    </Grid>
  );
};
